import { useQuery } from "@tanstack/vue-query";
import { format as dateFormat, differenceInDays, intervalToDuration, isBefore, parseISO } from "date-fns";
import { computed } from "vue";
import { useStore } from "vuex";
import { useConsolidatedAsyncState } from "@/composables/useConsolidatedAsyncState";
import ordersService from "@/services/orders";
import profileService from "@/services/profile";

const useExperiences = () => {
  const store = useStore();
  const participantId = computed(() => store.state.currentUser?.participantId);

  const applicationQueryKey = computed(() => ["applications", participantId.value]);
  const experiencesQueryKey = computed(() => ["experiences", participantId.value]);
  const profileQueryKey = computed(() => ["profile", participantId.value]);
  const profileDevicesQueryKey = computed(() => ["profileDevices", participantId.value]);

  const {
    data: applications,
    isLoading: applicationsIsLoading,
    error: applicationsError,
  } = useQuery({
    enabled: () => Boolean(participantId.value),
    queryKey: applicationQueryKey.value,
    queryFn: async () => {
      const response = await profileService.getApplicationsByParticipantId(participantId.value);
      return response.data?.items;
    },
  });

  const {
    data: experiences,
    isLoading: experiencesIsLoading,
    error: experiencesError,
  } = useQuery({
    enabled: () => Boolean(participantId.value),
    queryKey: experiencesQueryKey.value,
    queryFn: async () => {
      return await ordersService.getMyExperiencesSummary(participantId.value);
    },
  });

  const {
    data: profile,
    isLoading: profileIsLoading,
    error: profileError,
  } = useQuery({
    enabled: () => Boolean(participantId.value),
    queryKey: profileQueryKey.value,
    queryFn: async () => {
      const response = await profileService.getProfile(participantId.value);

      return response.data?.data;
    },
  });

  const {
    data: profileDevices,
    isLoading: devicesIsLoading,
    error: devicesError,
  } = useQuery({
    enabled: () => Boolean(participantId.value),
    queryKey: profileDevicesQueryKey.value,
    queryFn: async () => {
      const response = await profileService.getProfileDevices({
        extraParams: { participant_id: participantId.value },
      });

      return response?.data?.data?.items ?? [];
    },
  });

  const getExperienceDetails = (applicationId) => {
    const experienceDetailQueryKey = ["experienceDetail", applicationId];
    const {
      data: experienceDetail,
      isLoading: experienceDetailIsLoading,
      error: experienceDetailError,
    } = useQuery({
      enabled: () => Boolean(applicationId),
      queryKey: experienceDetailQueryKey,
      queryFn: async () => {
        return await ordersService.getMyExperiencesDetail(applicationId);
      },
    });
    return { experienceDetail, experienceDetailIsLoading, experienceDetailError };
  };

  const queryLoadingRefs = {
    applications: applicationsIsLoading,
    experiences: experiencesIsLoading,
    devices: devicesIsLoading,
    profile: profileIsLoading,
  };

  const queryErrorRefs = {
    applications: applicationsError,
    experiences: experiencesError,
    devices: devicesError,
    profile: profileError,
  };

  const { isLoading, hasErrors } = useConsolidatedAsyncState({
    queryLoading: queryLoadingRefs,
    queryErrors: queryErrorRefs,
  });

  const formatPrettyDate = (dateString) => {
    const date = parseISO(dateString);

    return dateFormat(date, "MMM d, yyyy");
  };

  const showStartMessage = (date) => {
    if (!date) {
      return false;
    }
    const startDate = date instanceof Date ? date : parseISO(date); // parses "yyyy-MM-dd" safely
    const today = new Date();

    return !isBefore(startDate, today);
  };

  const programStartMessage = (dateString) => {
    const startDate = parseISO(dateString); // parses "yyyy-MM-dd" safely
    const today = new Date();

    if (isBefore(startDate, today)) {
      return "Program has already started.";
    }

    const daysUntilStart = differenceInDays(startDate, today);

    if (daysUntilStart <= 90) {
      return `Starting in ${daysUntilStart} day${daysUntilStart !== 1 ? "s" : ""}`;
    }

    const duration = intervalToDuration({ start: today, end: startDate });

    if (daysUntilStart >= 365) {
      return (
        `Starting in ${duration.years} year${duration.years !== 1 ? "s" : ""}, ` +
        `${duration.months} month${duration.months !== 1 ? "s" : ""}, ` +
        `${duration.days} day${duration.days !== 1 ? "s" : ""}`
      );
    }

    return (
      `Starting in ${duration.months} month${duration.months !== 1 ? "s" : ""}, ` +
      `${duration.days} day${duration.days !== 1 ? "s" : ""}`
    );
  };

  return {
    participantId,
    profile,
    profileDevices,
    experiences,
    experiencesIsLoading: isLoading,
    experiencesError: hasErrors,
    getExperienceDetails,
    programStartMessage,
    showStartMessage,
    formatPrettyDate,
  };
};

export default useExperiences;
